import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskCategoryPageConfig } from '@front-platform/content-builder';
import { kioskCategoryPageResolveConfig } from '@front-platform/content-builder';
import { ViewportScroller } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ProductStoreDispatcher } from '@common/dispatchers/product.dispatcher';
import { ShopStoreRefiner } from '@common/refiners/shop.refiner';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';
import { TreeTab } from '@core/models/tab-list.model';
import { ModalsService } from '@core/services/modals/modals.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { Store } from '@ngrx/store';
import { staggeredListAnimation } from '@shared/animations/list.animation';
import { ScrollSpyDirective } from '@shared/directives/scroll-spy.directive';
import { DoodCategoryWithProduct } from '@shared/interfaces/category.interface';
import {
  KioskProductOverviewModalComponent,
  KioskProductOverviewModalConfig,
} from '@shared/modals/kiosk-product-overview-modal/kiosk-product-overview-modal.component';
import { treeListToTreeTabList } from '@shared/utils/tree/tree-to-tree-tabs';
import type { TabListAtomConfigInput } from '@shared/atoms/tab-list-atom/tab-list-atom.component';
import { KioskGlobalSettingsService } from '@store/kiosk-global-settings/kiosk-global-settings.service';
import { PRODUCT_STORE_COMPONENT_ACTIONS } from '@store/product/product.action';
import { combineLatest, filter, map, Subscription, tap } from 'rxjs';
import { KioskCategoryPageService } from './kiosk-category-page.service';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { KioskCategoryPagePreviewDataSource } from './kiosk-category-page-preview-data-source';
import { provideKioskCategoryPagePreviewDataSource } from './kiosk-category-page-data-source.providers';
import {
  catalogFilterKey,
  productMatchesCatalogFilters,
  type CatalogFilterSelection,
} from '@shared/blocks/shop-product-list-block/catalog-filters';
import { ShopCatalogFiltersService } from '@shared/blocks/shop-product-list-block/shop-catalog-filters.service';
export interface KioskCategoryPageRuntimeConfig {
  'categories_background_color'?: IContentBuilderFieldColor;
  'categories_active_color'?: IContentBuilderFieldColor;
  'categories_inactive_color'?: IContentBuilderFieldColor;
}

export type KioskCategoryPageConfigInput = Partial<
  Omit<KioskCategoryPageConfig, keyof KioskCategoryPageRuntimeConfig> &
    KioskCategoryPageRuntimeConfig
>;
export type KioskCategoryPageResolvedConfig = Omit<
  KioskCategoryPageConfig,
  keyof KioskCategoryPageRuntimeConfig
> &
  Partial<KioskCategoryPageRuntimeConfig>;
export interface KioskCategoryPageContract extends ConfigComponentContract<
  KioskCategoryPageConfigInput,
  KioskCategoryPageResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-category-page',
  templateUrl: './kiosk-category-page.component.html',
  animations: [staggeredListAnimation],
  standalone: false,
  providers: [KioskCategoryPageService, ...provideKioskCategoryPagePreviewDataSource()],
})
export class KioskCategoryPageComponent
  implements AfterViewInit, OnDestroy, KioskCategoryPageContract
{
  readonly config = input<KioskCategoryPageConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskCategoryPageConfig,
    KioskCategoryPageRuntimeConfig
  >(kioskCategoryPageResolveConfig, () => ({ config: this.config() }));
  readonly textColor = computed(
    () => this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly cardColor = computed(
    () => this.resolvedConfig()['card_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly buttonBackgroundColor = computed(
    () => this.resolvedConfig()['button_background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly buttonTextColor = computed(
    () => this.resolvedConfig()['button_text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly priceColor = computed(
    () => this.resolvedConfig()['price_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly unavailableButtonBackground = computed(
    () =>
      this.resolvedConfig()[
        'unavailable_button_background'
      ] as unknown as IContentBuilderFieldColor,
  );
  readonly unavailableButtonText = computed(
    () => this.resolvedConfig()['unavailable_button_text'] as unknown as IContentBuilderFieldColor,
  );
  readonly categoriesBackgroundColor = computed(
    () =>
      this.resolvedConfig()['categories_background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly categoriesActiveColor = computed(
    () => this.resolvedConfig()['categories_active_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly categoriesInactiveColor = computed(
    () =>
      this.resolvedConfig()['categories_inactive_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly secondaryColor = computed(
    () => this.resolvedConfig()['secondary_color'] as unknown as IContentBuilderFieldColor,
  );
  private readonly isPreview = inject(Cb2PreviewStateService).isActive();

  protected readonly tabListAtomConfig = computed((): TabListAtomConfigInput => ({
    padding_x: false,
    categories_background_color: this.categoriesBackgroundColor(),
    categories_active_color: this.categoriesActiveColor(),
    categories_inactive_color: this.categoriesInactiveColor(),
  }));

  protected readonly categoryWithProducts = signal<DoodCategoryWithProduct | null>(null);
  protected readonly activeItemId = signal<string | undefined>(undefined);
  protected readonly tabsHeight = signal<number>(0);
  protected readonly subCategoriesTabsSignal = signal<TreeTab[]>([]);
  protected readonly previewSubCategoriesWithProducts = signal<DoodCategoryWithProduct[]>([]);
  protected readonly productCardColumns = computed<number>(() => {
    return this.kioskGlobalSettingsService.productCardColSpan() === 2 ? 2 : 3;
  });

  @ViewChild('scrollContainer', { static: false }) scrollContainer?: ElementRef<HTMLElement>;
  @ViewChild('tabsContainer', { static: false }) tabsContainer?: ElementRef<HTMLElement>;
  @ViewChild('appScrollSpy', { static: false }) appScrollSpy?: ScrollSpyDirective;

  private categoryId: string | undefined = undefined;
  private readonly categoryIdSignal = signal<string | undefined>(undefined);
  private categorySubscription?: Subscription;
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly shopRefiner = inject(ShopStoreRefiner);
  private readonly flattenProductsWithCategories = toSignal(
    this.shopRefiner.selectActiveFlattenProductsWithCategories,
    { initialValue: [] },
  );
  private readonly treeCatalogCategories = toSignal(
    this.shopRefiner.selectActiveShopEnabledTreeCatalogCategory,
    { initialValue: [] },
  );

  private readonly modalsService =
    inject<ModalsService<[KioskProductOverviewModalConfig]>>(ModalsService);
  private readonly productDispatcher = inject(ProductStoreDispatcher);
  private readonly routerHelperService = inject(RouterHelperService);
  private readonly orderSelector = inject(OrderStoreSelector);
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly kioskGlobalSettingsService = inject(KioskGlobalSettingsService);
  private readonly store = inject(Store);
  private readonly previewDataSource = inject(KioskCategoryPagePreviewDataSource);

  private readonly liveSubCategoriesWithProducts = computed(() => {
    const flattenCategories = this.flattenProductsWithCategories();
    const treeCategories = this.treeCatalogCategories();
    const categoryId = this.categoryIdSignal();

    if (!categoryId || !flattenCategories?.length || !treeCategories?.length) {
      return [];
    }

    const currentCategoryTree = this.findCategoryInTree(treeCategories, categoryId);
    if (!currentCategoryTree?.children?.length) {
      return [];
    }

    // Get all subcategory IDs from the current category's children
    const getSubCategoryIds = (children: typeof treeCategories): string[] => {
      const ids: string[] = [];
      children.forEach(child => {
        if (child.category?.id) {
          ids.push(child.category.id);
        }
        if (child.children?.length) {
          ids.push(...getSubCategoryIds(child.children));
        }
      });
      return ids;
    };

    const subCategoryIds = getSubCategoryIds(currentCategoryTree.children);

    // Filter flattened categories to get only subcategories that belong to the current category
    return flattenCategories.filter(
      category => subCategoryIds.includes(category.id) && category.productsInformation?.length,
    );
  });

  protected readonly subCategoriesWithProducts = computed(() =>
    this.isPreview ? this.previewSubCategoriesWithProducts() : this.liveSubCategoriesWithProducts(),
  );

  protected readonly catalogFilters = inject(ShopCatalogFiltersService);

  private filterVisibleProducts(
    products: DoodProductModel[] | null | undefined,
  ): DoodProductModel[] {
    const filters = this.catalogFilters.applied();
    const list = (products ?? []).filter((product): product is DoodProductModel => !!product);
    if (!filters.length) {
      return list;
    }
    return list.filter(product => productMatchesCatalogFilters(product, filters));
  }

  protected readonly visibleCategoryProducts = computed(() =>
    this.filterVisibleProducts(this.categoryWithProducts()?.productsInformation),
  );

  protected readonly visibleSubCategoriesWithProducts = computed(() =>
    this.subCategoriesWithProducts()
      .map(subCategory => ({
        ...subCategory,
        productsInformation: this.filterVisibleProducts(subCategory.productsInformation),
      }))
      .filter(subCategory => subCategory.productsInformation.length > 0),
  );

  protected readonly visibleSubCategoryTabs = computed(() => {
    const visibleIds = new Set(
      this.visibleSubCategoriesWithProducts().map(subCategory => subCategory.id),
    );
    return this.subCategoriesTabsSignal().filter(tab => visibleIds.has(tab.id));
  });

  protected readonly subCategoriesIds = computed(() => {
    return this.visibleSubCategoriesWithProducts().map(subCategory => subCategory.id);
  });

  protected removeCatalogFilter(filter: CatalogFilterSelection): void {
    const key = catalogFilterKey(filter);
    this.catalogFilters.apply(
      this.catalogFilters.applied().filter(item => catalogFilterKey(item) !== key),
    );
  }

  protected clearCatalogFilters(): void {
    this.catalogFilters.clear();
  }

  constructor() {
    if (this.isPreview) {
      this.previewDataSource.vm$.pipe(takeUntilDestroyed()).subscribe(vm => {
        if (!vm) {
          this.categoryWithProducts.set(null);
          this.previewSubCategoriesWithProducts.set([]);
          this.subCategoriesTabsSignal.set([]);
          return;
        }

        this.categoryId = vm.categoryId;
        this.categoryIdSignal.set(vm.categoryId);
        this.categoryWithProducts.set(vm.categoryWithProducts);
        this.previewSubCategoriesWithProducts.set([...vm.subCategoriesWithProducts]);
        this.subCategoriesTabsSignal.set(
          vm.subCategoriesWithProducts.map(subCategory => ({
            id: subCategory.id,
            name: subCategory.name,
            icon: subCategory.icon,
            level: subCategory.level ?? 0,
            children: [],
          })),
        );
        this.activeItemId.set(vm.subCategoriesWithProducts[0]?.id);
        setTimeout(() => {
          this.updateTabsHeight();
        }, 100);
      });
      return;
    }

    // Get subcategories from tree structure
    const categoryId$ = toObservable(this.categoryIdSignal);
    const subCategoriesTabs$ = combineLatest([
      this.shopRefiner.selectActiveShopEnabledTreeCatalogCategory,
      categoryId$,
    ]).pipe(
      map(([treeCategories, categoryId]) => {
        if (!categoryId || !treeCategories?.length) {
          return [];
        }

        const currentCategoryTree = this.findCategoryInTree(treeCategories, categoryId);
        if (!currentCategoryTree?.children?.length) {
          return [];
        }

        // Convert children to TreeTab[]
        return treeListToTreeTabList({
          trees: currentCategoryTree.children,
          entityToTab: ({ category }) => ({
            id: category.id,
            name: category.name,
            icon: category.icon,
          }),
        });
      }),
    );
    subCategoriesTabs$.pipe(takeUntilDestroyed()).subscribe(tabs => {
      this.subCategoriesTabsSignal.set(tabs);
      setTimeout(() => {
        this.updateTabsHeight();
      }, 100);
    });

    effect(
      () => {
        // Trigger effect when subcategories change
        this.subCategoriesTabsSignal();
        // Update tabs height when subcategories change
        setTimeout(() => {
          this.updateTabsHeight();
        }, 100);
      },
      { allowSignalWrites: true },
    );

    // Initialize category ID and setup router subscriptions
    this.updateCategoryId();

    this.router.events
      .pipe(
        takeUntilDestroyed(),
        filter(event => event instanceof NavigationEnd),
        tap(() => this.updateCategoryId()),
      )
      .subscribe();

    this.route.params.pipe(takeUntilDestroyed()).subscribe(() => this.updateCategoryId());
  }

  ngAfterViewInit(): void {
    this.updateTabsHeight();
  }

  private updateTabsHeight(): void {
    const tabsContainer = this.tabsContainer?.nativeElement;
    if (tabsContainer) {
      this.tabsHeight.set(tabsContainer.offsetHeight);
    } else {
      this.tabsHeight.set(0);
    }
  }

  private updateCategoryId(): void {
    const categoryId: string | null = this.getCategoryIdFromRoute(this.route);
    if (categoryId) {
      // Reset scroll when category changes
      if (this.categoryId && this.categoryId !== categoryId) {
        this.resetScroll();
      }
      this.categoryId = categoryId;
      this.categoryIdSignal.set(categoryId);
      // Unsubscribe previous subscription if exists
      if (this.categorySubscription) {
        this.categorySubscription.unsubscribe();
      }
      this.categorySubscription = this.shopRefiner
        .selectCategoryById(categoryId)
        .subscribe(category => {
          this.categoryWithProducts.set(category);
          // Update tabs height and reset scroll after category is loaded
          setTimeout(() => {
            this.updateTabsHeight();
            this.resetScroll();
          }, 100);
        });
    } else {
      this.categoryId = undefined;
      this.categoryIdSignal.set(undefined);
      this.categoryWithProducts.set(null);
      // Unsubscribe when category is cleared
      if (this.categorySubscription) {
        this.categorySubscription.unsubscribe();
        this.categorySubscription = undefined;
      }
    }
  }

  private resetScroll(): void {
    const scrollContainer = this.scrollContainer?.nativeElement;
    if (scrollContainer) {
      // Reset transform and scroll
      scrollContainer.style.transform = '';
      scrollContainer.style.transition = '';
      scrollContainer.style.clipPath = '';
      scrollContainer.style.maxHeight = '';
      scrollContainer.style.height = '';
      scrollContainer.scrollTop = 0;
    }
  }

  private getCategoryIdFromRoute(route: ActivatedRoute): string | null {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot.paramMap.get('categoryId');
  }

  /**
   * Find a category in a tree structure by its ID
   */
  private findCategoryInTree<T extends { category?: { id: string }; children?: T[] }>(
    trees: T[],
    categoryId: string,
  ): T | null {
    for (const tree of trees) {
      if (tree.category?.id === categoryId) {
        return tree;
      }
      if (tree.children?.length) {
        const found = this.findCategoryInTree(tree.children, categoryId);
        if (found) return found;
      }
    }
    return null;
  }

  addProductDispatcher(product: DoodProductModel): void {
    this.store.dispatch(
      PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct({
        productId: product.id,
        productShopId: product.shop_id,
        productOrigin: {
          channel: 'catalog',
          reference: `category_${this.categoryId}`,
        },
        categoryId: this.categoryId,
      }),
    );
  }

  openProductModal(product: DoodProductModel): void {
    this.productDispatcher.insertActive(product);
    this.modalsService.open(KioskProductOverviewModalComponent.handle, undefined, {
      image: product.images?.cover?.url,
      name: product.name,
      description: product.description,
      allergens: product.allergens,
      type: product.type,
      colors: {
        background: this.buttonTextColor(),
        accent: this.secondaryColor(),
      },
    });
  }

  openAddProductModal(product: DoodProductModel): void {
    this.productDispatcher.insertActive(product);
    this.modalsService.open(KioskProductOverviewModalComponent.handle, undefined, {
      image: product.images?.cover?.url,
      name: product.name,
      description: product.description,
      allergens: product.allergens,
      price: product.price + '',
      type: product.type,
      colors: {
        background: this.buttonTextColor(),
        accent: this.secondaryColor(),
      },
      navigateToProduct: () =>
        this.store.dispatch(
          PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct({
            productId: product.id,
            productShopId: product.shop_id,
            categoryId: this.categoryId,
            productOrigin: {
              channel: 'catalog',
              reference: `category_${this.categoryId}`,
            },
          }),
        ),
    });
  }

  back(): void {
    const shopCity = this.route.snapshot.paramMap.get('shopCity');
    const shopType = this.route.snapshot.paramMap.get('shopType');
    const shopSlug = this.route.snapshot.paramMap.get('shopSlug');

    this.router.navigate([
      this.routerHelperService.translateRoute(`${shopCity}/${shopType}/${shopSlug}`),
    ]);
  }

  protected unAvailableProductById = toSignal(
    this.orderSelector.selectErrors.pipe(
      map(errors =>
        errors.violations
          ?.filter(
            violation =>
              violation.code &&
              [
                'PRODUCT_NOT_AVAILABLE_FOR_DISTRIBUTION_MODE',
                'PRODUCT_HIDDEN',
                'PRODUCT_NOT_AVAILABLE',
              ].includes(violation.code),
          )
          .map(violation => violation.data?.id as string)
          .reduce(
            (acc, id) => {
              acc[id] = true;
              return acc;
            },
            {} as Record<string, boolean>,
          ),
      ),
    ),
  );

  onTabClick({ tabId }: { tabId: string }): void {
    const scrollContainer = this.scrollContainer?.nativeElement;
    if (!scrollContainer) {
      // Fallback to default scroll behavior
      this.viewportScroller.setOffset([0, 100]);
      this.viewportScroller.scrollToAnchor(tabId);
      return;
    }

    // Check if container is scrollable
    const containerVisibleHeight = scrollContainer.clientHeight;
    const containerScrollHeight = scrollContainer.scrollHeight;
    const isScrollable = containerScrollHeight > containerVisibleHeight;

    // If scrollable, use appScrollSpy
    if (isScrollable && this.appScrollSpy) {
      this.appScrollSpy.scrollToSectionId(tabId);
      return;
    }

    // If not scrollable, use transform to visually move content (responsive mode)
    setTimeout(() => {
      const targetElement = document.getElementById(tabId);
      if (!targetElement) {
        return;
      }

      const tabsHeight = this.tabsHeight();
      const elementRect = targetElement.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementTopRelativeToContainer =
        elementRect.top - containerRect.top + scrollContainer.scrollTop;

      const offset = tabsHeight - 72;
      const offsetNeeded = -elementTopRelativeToContainer + offset;

      // Save original styles
      const originalTransition = scrollContainer.style.transition;
      const originalOverflow = scrollContainer.style.overflow;

      // Apply overflow hidden and clip-path to hide content behind tabs
      scrollContainer.style.overflow = 'hidden';
      if (tabsHeight > 0) {
        scrollContainer.style.clipPath = `inset(${tabsHeight}px 0 0 0)`;
      }

      // Apply transform to visually move the content
      scrollContainer.style.transition = 'transform 0.4s ease-in-out';
      scrollContainer.style.transform = `translateY(${offsetNeeded}px)`;

      // After animation completes, try to make it permanent with real scroll
      setTimeout(() => {
        const originalMaxHeight = scrollContainer.style.maxHeight;
        const originalHeight = scrollContainer.style.height;

        scrollContainer.style.maxHeight = `${containerVisibleHeight}px`;
        scrollContainer.style.height = `${containerVisibleHeight}px`;
        scrollContainer.style.overflow = 'auto';

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            void scrollContainer.offsetHeight;

            // If now scrollable, use real scroll and remove transform
            if (scrollContainer.scrollHeight > scrollContainer.clientHeight && this.appScrollSpy) {
              scrollContainer.style.transform = '';
              scrollContainer.style.clipPath = '';
              this.appScrollSpy.scrollToSectionId(tabId);

              // Restore styles
              setTimeout(() => {
                scrollContainer.style.maxHeight = originalMaxHeight;
                scrollContainer.style.height = originalHeight;
                scrollContainer.style.overflow = originalOverflow;
                scrollContainer.style.transition = originalTransition;
              }, 100);
            } else {
              // Keep transform and clip-path
              setTimeout(() => {
                scrollContainer.style.maxHeight = originalMaxHeight;
                scrollContainer.style.height = originalHeight;
                scrollContainer.style.overflow = originalOverflow;
              }, 100);
            }
          });
        });
      }, 400);
    }, 200);
  }

  ngOnDestroy(): void {
    // Unsubscribe from category subscription
    // Router subscriptions are automatically cleaned up by takeUntilDestroyed()
    if (this.categorySubscription) {
      this.categorySubscription.unsubscribe();
    }
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskCategoryPageComponent, { 'keyAliases': {} });
